// Supported locales, injected from the server via the `locales` meta tag in
// app/views/shared/_meta.html.erb (launched locales, plus preview locales for
// flag-holders — the same routable set as the server). Adding a locale to
// app/models/locale.rb propagates here automatically — no edit needed. The
// hardcoded list is only a fallback for contexts without the meta tag (tests,
// or scripts running before the head is parsed).
function knownLocalesFromMeta () {
  if (typeof document === 'undefined') return ['en', 'nl-nl']
  const meta = document.querySelector('meta[name="locales"]')
  if (!meta || !meta.content) return ['en', 'nl-nl']
  return meta.content.split(',').map(code => code.trim().toLowerCase()).filter(Boolean)
}

const KNOWN_LOCALES = new Set(knownLocalesFromMeta())

/**
 * Check if a path segment is a known locale (e.g., 'en', 'nl-nl')
 * @param {string} segment - Path segment to test
 * @returns {boolean}
 */
export function isLocaleSegment (segment) {
  return segment ? KNOWN_LOCALES.has(segment.toLowerCase()) : false
}

/**
 * Strip locale prefix from pathname if present
 * e.g., '/nl-nl/furniture' -> '/furniture'
 * e.g., '/furniture' -> '/furniture'
 * @param {string} pathname - URL pathname (with leading slash)
 * @returns {string} - Pathname without locale prefix
 */
export function stripLocaleFromPath (pathname) {
  if (!pathname) return pathname

  const segments = pathname.split('/').filter(Boolean)
  if (segments.length > 0 && isLocaleSegment(segments[0])) {
    return '/' + segments.slice(1).join('/')
  }
  return pathname
}

/**
 * Get locale from pathname if present
 * e.g., '/nl-nl/furniture' -> 'nl-nl'
 * e.g., '/furniture' -> null
 * @param {string} pathname - URL pathname (with leading slash)
 * @returns {string|null} - Locale string or null if not present
 */
export function getLocaleFromPath (pathname) {
  if (!pathname) return null

  const segments = pathname.split('/').filter(Boolean)
  if (segments.length > 0 && isLocaleSegment(segments[0])) {
    return segments[0]
  }
  return null
}

/**
 * Prepend locale to path if provided (and not default 'en')
 * @param {string} path - URL path (with or without leading slash)
 * @param {string|null} locale - Locale to prepend (e.g., 'nl-nl')
 * @returns {string} - Path with locale prepended if applicable
 */
export function prependLocale (path, locale) {
  if (!locale || locale === 'en') return path

  const normalizedPath = path.startsWith('/') ? path : '/' + path
  return '/' + locale + normalizedPath
}

// Current app locale (e.g. 'en', 'fr-fr', 'nl-nl'), read from the body data
// attribute set server-side from I18n.locale, falling back to the URL path.
export function currentLocale () {
  const fromBody = typeof document !== 'undefined' && document.body ? document.body.dataset.locale : null
  const fromPath = typeof window !== 'undefined' ? getLocaleFromPath(window.location.pathname) : null
  return (fromBody || fromPath || 'en').toLowerCase()
}

// Map the app locale to a Stripe Elements locale code. Stripe localises its own
// field placeholders, validation errors and postcode labels from this. Unknown
// locales fall back to 'auto' so Stripe infers from the browser.
const STRIPE_LOCALES = { en: 'en', 'fr-fr': 'fr', 'nl-nl': 'nl' }

export function stripeLocale () {
  return STRIPE_LOCALES[currentLocale()] || 'auto'
}

export function hasHreflangAlternates () {
  return !!document.querySelector('link[rel="alternate"][hreflang]')
}

// hreflang is the locale code in canonical BCP-47 casing (Locale#hreflang: nl-nl -> nl-NL), so match case-insensitively.
export function localizedAlternateUrl (locale) {
  const target = locale.toLowerCase()
  const links = document.querySelectorAll('link[rel="alternate"][hreflang]')
  const match = Array.from(links).find(link => link.getAttribute('hreflang').toLowerCase() === target)
  return match ? match.getAttribute('href') : null
}
